
import Guard from 'arg-guard'

/**
 * @export JWT
 * @class JWT
 */
export default class JWT {
    /**
     * Creates an instance of JWT and parses it from the supplied token.
     * @param {string} token
     * @memberOf JWT
     */
    constructor(token) {
        this.token = token
        this.data = token && token !== 'unused' ? JWT.parse(token) : { exp: 0 }
    }

    /**
     * Returns true if the token has expired.
     *
     * @returns {boolean}
     */
    get isExpired() {
        return this.data.exp * 1000 < Date.now()
    }

    /**
     * Returns the token's expiry time as a Date object.
     *
     * @returns {Date}
     */
    get expiryDate() {
        return new Date(this.data.exp * 1000)
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     *
     * @static
     * @param {?Object} data Source data from state.
     * @returns {JWT} Either the data is passed back if already of this type or the result of the constructor is.
     */
    static ensure(data = null) {
        return data && data instanceof JWT ? data : new JWT(data)
    }

    /**
     * Accept a JWT and parse it and return the object.
     *
     * @param {string} token The JWT token to parse.
     * @returns {Object}
     */
    static parse(token) {
        Guard.throwIf(token, 'token')

        try {
            const base64Url = token.split('.')[1]
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
            const jsonPayload = decodeURIComponent(
                window.atob(base64).split('').map(char => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2)).join('')
            )

            return JSON.parse(jsonPayload)
        } catch {
            throw 'Failed to parse token.'
        }
    }
}
